import { useCallback } from 'react';
import { useFocusEffect } from '@react-navigation/native';

import { useIsLoggedIn } from '@order/AuthMachine';
import { type PaymentMethod, useCustomerQuery } from '@order/graphql';
import { usePaymentMethods } from '@order/hooks';

/**
 * Refetch Customer to access QRCode that is only generated after payment method.
 * This can't be a resolver update because adding a payment method doesn't return a QRCode.
 *
 * Additionally, when adding a payment method from elsewhere, like the account screen,
 * It would be premature to fetch the QRCode as we don't know if Scan To Pay will be used,
 * So if there are billing accounts but there isn't a QRCode, we refetch the customer on focus.
 */
export function useRefetchCustomerAfterPaymentMethodCreation() {
  const [customerResponse, refetchCustomer] = useCustomerQuery({
    requestPolicy: 'cache-only',
    pause: !useIsLoggedIn(),
  });

  // ─── QRCode ──────────────────────────────────────────────────────────

  const qrCode = customerResponse.data?.currentCustomer?.qrCode;
  const isLoadingQrCode = customerResponse.fetching || customerResponse.stale;

  // ─── Payment Methods ─────────────────────────────────────────────────

  const { paymentMethods, isLoadingPaymentMethods } = usePaymentMethods();
  const hasPaymentMethods = paymentMethods.length > 0;
  const wasPaymentMethodJustAdded = hasPaymentMethods && !qrCode;

  // ─── Loading State ───────────────────────────────────────────────────

  const isLoading = isLoadingPaymentMethods || isLoadingQrCode;

  // ─── Callbacks ───────────────────────────────────────────────────────

  const onSuccess = useCallback(
    (_paymentMethod: PaymentMethod) => {
      refetchCustomer({ requestPolicy: 'cache-and-network' });
    },
    [refetchCustomer],
  );

  const fetchQrCodeAfterPaymentMethodCreation = useCallback(() => {
    if (wasPaymentMethodJustAdded) {
      refetchCustomer({ requestPolicy: 'cache-and-network' });
    }
  }, [wasPaymentMethodJustAdded, refetchCustomer]);

  useFocusEffect(fetchQrCodeAfterPaymentMethodCreation);

  return { isLoading, qrCode, paymentMethods, hasPaymentMethods, onSuccess };
}
